<template>
  <div class="account-select">
    <v-select
      :value="value"
      v-bind="$attrs"
      class="account-select__input"
      :class="{ 'account-select__input--dense': singleLine }"
      :menu-props="{ contentClass: 'account-select__content' }"
      :items="accountOptions"
      :item-text="getSelectedOptionText"
      item-value="accountId"
      :label="label"
      :single-line="singleLine"
      :hide-details="hideDetails"
      :disabled="selectDisabled"
      :error="error"
      :error-messages="errorMessages"
      @input="$emit('input', $event)"
      @change="$emit('change', $event)"
    >
      <template
        slot="item"
        slot-scope="data"
      >
        <template>
          <v-list-tile-content>
            <v-list-tile-title>
              <template v-if="data.item.accountId === null">{{ createOptionText }}</template>
              <template v-else-if="showName">
                <span>{{ data.item.name | accountName }} ({{ data.item.accountId }})</span>
              </template>
              <template v-else>
                <span>{{ data.item.accountId }}</span>
              </template>
            </v-list-tile-title>

            <v-list-tile-sub-title v-if="!hideBalance">
              <span
                v-for="(balance, key) in data.item.balances"
                :key="key"
                class="account-select__balance"
              >{{ balance.balance | number }} {{ balance.currency }}</span>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </template>
      </template>
    </v-select>

    <div
      v-if="!hideBalance"
      class="account-select__input-helper"
    >
      <template v-if="selectedAccount != null">
        <span class="font-weight-bold mr-1">Balance:</span>
        <span>
          <span
            v-for="(balance, key) in selectedAccount.balances"
            :key="key"
            class="account-select__balance"
          >{{ balance.balance | number }} {{ balance.currency }}</span>
        </span>
      </template>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'AccountSelect',
    inheritAttrs: false,
    props: {
      value: {
        required: true,
        validator: value => {
          return typeof value === 'string' || value === null
        }
      },
      accounts: {
        required: true,
        type: Array
      },
      label: {
        type: String,
        default: 'Select account nr.'
      },
      singleLine: {
        type: Boolean,
        default: false
      },
      autoReset: {
        type: Boolean,
        default: false
      },
      hideDetails: {
        type: Boolean,
        default: true
      },
      showName: {
        type: Boolean,
        default: false
      },
      hideBalance: {
        type: Boolean,
        default: false
      },
      addCreateOption: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      error: {
        type: Boolean,
        default: false
      },
      errorMessages: {
        type: String,
        default: undefined
      }
    },
    data () {
      return {
        createOptionText: 'New account'
      }
    },
    computed: {
      activeAccounts () {
        return this.accounts.filter(account => !account.block)
      },
      accountOptions () {
        return (this.addCreateOption === true)
          ? [ { accountId: null }, ...this.activeAccounts ]
          : this.activeAccounts
      },
      hasOnlyOneActiveAccount () {
        return this.activeAccounts.length === 1
      },
      selectedAccount () {
        return this.accounts.find(account => this.value === account.accountId)
      },
      selectDisabled () {
        return this.disabled || (!this.addCreateOption && this.value != null && this.value !== '' && this.hasOnlyOneActiveAccount)
      }
    },
    watch: {
      accounts () {
        if (this.autoReset === true) {
          this.resetSelectedAccount()
        }
      }
    },
    methods: {
      getSelectedOptionText (option) {
        if (option.accountId === null) {
          return this.createOptionText
        }

        return this.showName ? `${this.$options.filters.accountName(option.name)} (${option.accountId})` : option.accountId
      },
      resetSelectedAccount () {
        const selectedAccount = (!this.addCreateOption && this.hasOnlyOneActiveAccount)
          ? this.activeAccounts[0].accountId
          : ''

        this.$emit('input', selectedAccount)
        this.$emit('change', selectedAccount)
      }
    }
  }
</script>

<style lang="scss">
  .account-select {
    &__input {
      &--dense {
        padding-top: 0;
        margin-top: 0;
      }
    }

    &__balance {
      display: inline-block;
      color: #f44336;
      font-weight: 700;

      &::after {
        content: '·';
        margin: 0 4px;
      }

      &:last-child {
        &::after {
          display: none;
        }
      }
    }

    &__account-type {
      position: relative;
      bottom: 1px;
      height: 18px;
      line-height: 18px;
      font-size: 10px;
      border-radius: 0;
      margin: 0 2px 0 5px;

      & .v-chip__content {
        height: auto;
      }
    }

    &__content {
      .v-list {
        padding: 15px 0 10px;
      }

      .v-list__tile {
        min-height: 55px;
        height: auto;
      }
    }

    &__input-helper {
      min-height: 18px;
      color: rgba(33, 33, 33, .54);
      font-size: 12px;
      margin-top: 4px;
    }
  }
</style>
